import { dateToISOString } from '../../utils/date'

/**
 * Session label for conditional scheduling UI.
 * Quest (Experience) SOWs use named session types; others use Session N.
 * @param {number|string} sessionIndex - 1-based session number
 * @param {string} [sowType] - SOW type (e.g. 'QUEST', 'REGULAR')
 * @returns {string}
 */
export const getConditionalSessionLabel = (sessionIndex, sowType) => {
  const idx = Number(sessionIndex)
  if (sowType === 'QUEST') {
    if (idx === 1) return 'Quest Preparation Session'
    if (idx === 2) return 'Quest Integration Session'
    if (idx === 3) return 'Regular Session'
  }
  return `Session ${idx}`
}

/**
 * Resolves scheduling flags for a coach from the coach options, falling back to stored flags
 * @param {Array} coaches - Coach options
 * @param {string} coachId - Coach id
 * @param {Object} coachSchedulingFlagsByCoachId - Stored flags keyed by coach id
 * @returns {Object} Scheduling flags
 */
export const getCoachSchedulingFlags = (
  coaches = [],
  coachId,
  coachSchedulingFlagsByCoachId = {},
) => {
  const coach = coaches?.find(
    (coachOption) =>
      coachOption?.value === coachId || coachOption?._id === coachId,
  )
  const storedFlags = coachSchedulingFlagsByCoachId?.[coachId] || {}

  return {
    isScheduleEnabled:
      coach?.isScheduleEnabled ?? storedFlags?.isScheduleEnabled,
    hasVideoCallLink: coach?.hasVideoCallLink ?? storedFlags?.hasVideoCallLink,
    hasCoachBio: coach?.hasCoachBio ?? storedFlags?.hasCoachBio,
    hasQuestBio: coach?.hasQuestBio ?? storedFlags?.hasQuestBio,
  }
}

/**
 * Formats scheduling conditions for API submission
 * @param {Array} schedulingConditions - Array of scheduling condition objects
 * @returns {Array} Formatted scheduling conditions ready for API
 */
export const formatSchedulingConditionsForAPI = (schedulingConditions) => {
  if (!schedulingConditions || !Array.isArray(schedulingConditions)) {
    return []
  }

  return schedulingConditions
    .filter(
      (condition) => condition && condition.condition && condition.primaryDate,
    )
    .map((condition) => {
      const formatted = {
        sessionIndex: condition.sessionIndex,
        condition: condition.condition,
        primaryDate: dateToISOString(condition.primaryDate, 'MM/DD/YYYY'),
      }
      if (condition.condition === 'between' && condition.secondaryDate) {
        formatted.secondaryDate = dateToISOString(
          condition.secondaryDate,
          'MM/DD/YYYY',
        )
      }
      return formatted
    })
}

/**
 * Prepares form values for submission, including scheduling conditions
 * @param {Object} values - Formik values
 * @returns {Object} Prepared form values
 */
export const prepareFormValuesForSubmission = (values) => {
  const formValues = {
    ...values,
    // Ensure sowType is always included
    sowType: values.sowType || 'REGULAR',
    // Ensure growthSessionDuration is included when includeGrowthSession is true
    ...(values.includeGrowthSession &&
      values.growthSessionDuration && {
        growthSessionDuration: Number(values.growthSessionDuration),
      }),
  }

  // Handle isPlatformScheduling - convert to boolean
  if (formValues.isPlatformScheduling !== undefined) {
    formValues.isPlatformScheduling =
      formValues.isPlatformScheduling === 'true' ||
      formValues.isPlatformScheduling === true ||
      (formValues.isPlatformScheduling !== false &&
        formValues.isPlatformScheduling !== 'false')
  }

  // Handle questId - only include when sowType is QUEST and isPlatformScheduling is true
  if (
    formValues.sowType === 'QUEST' &&
    (formValues.isPlatformScheduling === true ||
      formValues.isPlatformScheduling === 'true' ||
      formValues.isPlatformScheduling !== false)
  ) {
    // Include questId when sowType is QUEST and questId exists
    // Validation will catch if questId is missing
    if (formValues.questId) {
      // eslint-disable-next-line no-self-assign
      formValues.questId = formValues.questId
    }
    // Don't delete questId - keep it in formValues so validation can check it
  } else {
    delete formValues.questId
  }

  // Handle totalSession
  const shouldSendTotalSession =
    formValues.totalDurationOfContract &&
    (formValues.totalDurationOfContract !== 'Indefinite' ||
      formValues.totalDurationOfContract !== '')
  if (!shouldSendTotalSession) {
    delete formValues.totalSession
  }

  // Format schedulingConditions for API
  if (
    formValues.isConditionalSchedulingEnabled &&
    formValues.schedulingConditions
  ) {
    formValues.schedulingConditions = formatSchedulingConditionsForAPI(
      formValues.schedulingConditions,
    )
  } else {
    delete formValues.schedulingConditions
  }

  return formValues
}

/**
 * Why the scheduling email preview cannot be requested yet, or '' when it can
 * @param {Object} params
 * @param {Object} params.values - Formik values
 * @param {string} params.clientId - Client the SOW is for
 * @param {string} params.coachId - Coach chosen in the form
 * @returns {string} Reason shown to the admin, empty when the preview is available
 */
export const getSowEmailPreviewDisabledReason = ({
  values,
  clientId,
  coachId,
}) => {
  const isPlatformScheduling =
    values.isPlatformScheduling === true ||
    values.isPlatformScheduling === 'true'
  if (!isPlatformScheduling) {
    return 'The scheduling email is only sent with Platform Scheduling, so there is no preview for Manual Scheduling.'
  }
  if (!clientId || !coachId) {
    return 'Choose a coach to preview the scheduling email.'
  }
  if (values.sowType === 'QUEST' && !values.questId) {
    return 'Choose an Experience to preview the scheduling email.'
  }
  return ''
}

/**
 * Builds the body for POST /sows/email-preview from the SOW form values.
 * Only the fields the endpoint accepts are included.
 * @param {Object} params
 * @param {Object} params.values - Formik values
 * @param {string} params.clientId - Client the SOW is for
 * @param {string} params.coachId - Coach chosen in the form
 * @returns {Object} Request body
 */
export const buildSowEmailPreviewPayload = ({ values, clientId, coachId }) => {
  const isQuest = values.sowType === 'QUEST'
  const payload = {
    clientId,
    coachId,
    sowType: isQuest ? 'QUEST' : 'REGULAR',
    includeBioInMail: values.includeBioInMail === true,
  }

  if (isQuest) {
    payload.questId = values.questId
  }

  if (values.isConditionalSchedulingEnabled) {
    // The formatter keeps a between row without secondaryDate, which the API rejects
    const completeConditions = (values.schedulingConditions || []).filter(
      (condition) =>
        condition?.condition !== 'between' || condition?.secondaryDate,
    )
    payload.isConditionalSchedulingEnabled = true
    payload.schedulingConditions =
      formatSchedulingConditionsForAPI(completeConditions)
  }

  return payload
}
